import { LitElement, css, html } from 'lit';
import { customElement } from 'lit/decorators.js';

import { version } from '../../../../game/Version';
import { tokens } from '../../../theme/tokens.css';

/**
 * Lobby Footer - Bottom links for the lobby screen
 *
 * Displays the copyright years, links to Reddit, Discord, GitHub, Privacy and
 * Contact, and the version.
 *
 * @element lobby-footer
 */
@customElement('lobby-footer')
export class LobbyFooter extends LitElement {
  static override styles = [
    tokens,
    css`
      :host {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        align-items: center;
        gap: 6px;
        position: absolute;
        /* Centred by its margins, not left: 50% and a translate: a
           shrink-to-fit box at left: 50% only gets the half of the screen
           right of centre, which stacked this three or four lines deep on an
           upright phone. */
        left: 0;
        right: 0;
        margin: 0 auto;
        width: fit-content;
        max-width: 96vw;
        /* Clear of the home indicator on notched phones; env() is 0 elsewhere. */
        bottom: calc(20px + env(safe-area-inset-bottom, 0px));
        font-family: var(--font-family);
        /* Legacy's 12px. */
        font-size: var(--font-size-sm);
        text-shadow: var(--shadow-text);
        color: var(--color-text-tooltip);
        cursor: default;
        pointer-events: auto;
        -webkit-user-select: none;
        user-select: none;
        -webkit-touch-callout: none;
        /* Tapped on the mobile lobby — drop the grey tap flash; the :active
           white-text rule below already gives feedback. */
        -webkit-tap-highlight-color: transparent;
      }

      /* Pull tighter to the bottom edge when vertical space is constrained —
         mirrors the top-edge rule of airmash-lobby's top row. Reclaims ~16px
         of visible-area headroom for the lobby panel above. */
      @media (max-height: 500px) {
        :host {
          bottom: calc(4px + env(safe-area-inset-bottom, 0px));
        }
      }

      /* Legacy's #8c8c8c. */
      .copyright {
        position: relative;
        color: var(--color-text-tertiary);
        padding-left: 16px;
      }

      .copyright-symbol {
        position: absolute;
        display: inline-block;
        left: 0;
        top: -3px;
        font-size: var(--font-size-lg);
      }

      /* Keeps the browser's focus ring, which now shows only for keyboard
         focus. Legacy's outline: none hid the outline a click used to leave. */
      a {
        position: relative;
        display: inline-block;
        text-decoration: none;
        padding: 6px 10px;
        border-radius: var(--radius-md);
        color: var(--color-text-tooltip);
      }

      /* On touch each link reaches 3px into the 6px gaps around it, so the
         targets meet and a tap between two rows still lands on a link. */
      @media (hover: none) {
        a::after {
          content: '';
          position: absolute;
          inset: -3px;
        }
      }

      a:visited {
        color: var(--color-text-tooltip);
        text-decoration: none;
      }

      /* Hover rules gated to devices with real hover. Without this, iOS
         Safari treats the first tap as a hover-enter and sticks the styled
         state until the user taps somewhere else — which makes long-pressed
         links look stuck-filled even after release. */
      @media (hover: hover) {
        a:hover {
          color: var(--color-text-white);
          text-decoration: none;
          background-color: var(--color-bg-chip);
        }
      }

      a:active {
        color: var(--color-text-white);
        text-decoration: none;
      }

      /* Matches the copyright at the other end. */
      .version {
        color: var(--color-text-tertiary);
      }
    `,
  ];

  private get currentYear(): number {
    return new Date().getFullYear();
  }

  override render() {
    return html`
      <span class="copyright"><span class="copyright-symbol">©</span>2017-${this.currentYear}</span>
      <a href="https://www.reddit.com/r/airmash/" target="_blank" rel="noopener">Reddit</a>
      <a href="https://discord.gg/uGpG3C9rJN" target="_blank" rel="noopener">Discord</a>
      <a href="https://github.com/airmash-refugees/airmash-frontend/" target="_blank" rel="noopener">GitHub</a>
      <a href="/changelog.html" target="_blank" rel="noopener">Changelog</a>
      <a href="/privacy.html" target="_blank" rel="noopener">Privacy</a>
      <a href="/contact.html" target="_blank" rel="noopener">Contact</a>
      <span class="version">v${version}</span>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'lobby-footer': LobbyFooter;
  }
}
